var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { computed, observable } from "mobx";
import { NamedRoutes } from "../NamedRoutes";
import { Disposer } from "../../Infra/Disposer";
export class GlobalAdminQueueDetailViewModel {
    constructor(container) {
        this.disposer = new Disposer();
        this.queueStats = null;
        this.jobs = [];
        this.loading = false;
        this.selectedJob = null;
        this.stateFilter = "all";
        this.confirmAction = null;
        this.routingUIManager = container.routingUIManager;
        this.jobManagementManager = container.jobManagementManager;
        this.jobManagementService = container.jobManagementService;
    }
    run() {
        this.disposer.reaction(() => this.queueName, (queueName) => {
            if (queueName) {
                void this.loadData();
            }
        }, { fireImmediately: true, name: "loadQueueData" });
    }
    stop() {
        this.disposer.dispose();
    }
    get queueName() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.queueName) !== null && _b !== void 0 ? _b : "";
    }
    get actionLoading() {
        return this.jobManagementManager.actionLoading;
    }
    get lastActionResult() {
        return this.jobManagementManager.lastActionResult;
    }
    get filteredJobs() {
        if (this.stateFilter === "all") {
            return this.jobs;
        }
        return this.jobs.filter((job) => job.state === this.stateFilter);
    }
    async loadData() {
        if (!this.queueName)
            return;
        this.loading = true;
        try {
            const [stats, jobsResponse] = await Promise.all([
                this.jobManagementService.getQueueStats(this.queueName),
                this.jobManagementService.getQueueJobs(this.queueName, {
                    state: this.stateFilter === "all" ? undefined : this.stateFilter,
                    limit: 100,
                }),
            ]);
            this.queueStats = stats;
            this.jobs = jobsResponse.jobs;
        }
        catch (error) {
            console.error("Failed to load queue data:", error);
        }
        finally {
            this.loading = false;
        }
    }
    async loadJobs() {
        if (!this.queueName)
            return;
        try {
            const response = await this.jobManagementService.getQueueJobs(this.queueName, {
                state: this.stateFilter === "all" ? undefined : this.stateFilter,
                limit: 100,
            });
            this.jobs = response.jobs;
        }
        catch (error) {
            console.error("Failed to load jobs:", error);
        }
    }
    setStateFilter(filter) {
        this.stateFilter = filter;
        void this.loadJobs();
    }
    refresh() {
        void this.loadData();
        this.jobManagementManager.refresh();
    }
    onBack() {
        this.routingUIManager.navigateTo(NamedRoutes.GLOBAL_ADMIN_JOB_MANAGEMENT);
    }
    viewJobDetails(job) {
        this.selectedJob = job;
    }
    closeJobDetails() {
        this.selectedJob = null;
    }
    showRetryConfirmation(jobId) {
        this.confirmAction = { type: "retry", jobId };
    }
    showCancelConfirmation(jobId) {
        this.confirmAction = { type: "cancel", jobId };
    }
    showResumeConfirmation(jobId) {
        this.confirmAction = { type: "resume", jobId };
    }
    hideConfirmation() {
        this.confirmAction = null;
    }
    async confirmActionExecute() {
        if (!this.confirmAction)
            return;
        const { type, jobId } = this.confirmAction;
        try {
            switch (type) {
                case "retry":
                    await this.jobManagementManager.retryJob(jobId);
                    break;
                case "cancel":
                    await this.jobManagementManager.cancelJob(jobId);
                    break;
                case "resume":
                    await this.jobManagementManager.resumeJob(jobId);
                    break;
            }
            await this.loadData();
        }
        finally {
            this.confirmAction = null;
        }
    }
    clearActionResult() {
        this.jobManagementManager.clearLastActionResult();
    }
    canRetry(job) {
        if (job.state === "failed") {
            return job.retryCount >= job.retryLimit;
        }
        return job.state === "cancelled" || job.state === "expired";
    }
    canCancel(job) {
        return job.state === "created" || job.state === "retry" || job.state === "active";
    }
    canResume(job) {
        return job.state === "cancelled";
    }
    formatDate(dateValue) {
        if (dateValue === null || dateValue === undefined)
            return "-";
        const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
        return date.toLocaleString();
    }
    formatRelativeTime(dateValue) {
        if (dateValue === null || dateValue === undefined)
            return "-";
        const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays > 0)
            return `${diffDays}d ago`;
        if (diffHours > 0)
            return `${diffHours}h ago`;
        if (diffMins > 0)
            return `${diffMins}m ago`;
        return `${diffSecs}s ago`;
    }
}
__decorate([
    observable
], GlobalAdminQueueDetailViewModel.prototype, "queueStats", void 0);
__decorate([
    observable
], GlobalAdminQueueDetailViewModel.prototype, "jobs", void 0);
__decorate([
    observable
], GlobalAdminQueueDetailViewModel.prototype, "loading", void 0);
__decorate([
    observable
], GlobalAdminQueueDetailViewModel.prototype, "selectedJob", void 0);
__decorate([
    observable
], GlobalAdminQueueDetailViewModel.prototype, "stateFilter", void 0);
__decorate([
    observable
], GlobalAdminQueueDetailViewModel.prototype, "confirmAction", void 0);
__decorate([
    computed
], GlobalAdminQueueDetailViewModel.prototype, "queueName", null);
__decorate([
    computed
], GlobalAdminQueueDetailViewModel.prototype, "actionLoading", null);
__decorate([
    computed
], GlobalAdminQueueDetailViewModel.prototype, "lastActionResult", null);
__decorate([
    computed
], GlobalAdminQueueDetailViewModel.prototype, "filteredJobs", null);
